<script>
  import {
    NewBsBox,
    BeerTable,
    BeerButton,
    Modal as BeerModal,
    Icon as BeerIcon,
    Tag as BeerTag,
    withDialog,
    toaster,
  } from '@xhs/yam-beer'
  import Search from '@xhs/yam-beer/cmp/dataEntry/Search/Search'
  import Pagination from '@xhs/yam-beer/cmp/dataEntry/Pagination/Pagination'
  import BeerForm from '@xhs/beer-formily'

  import ToolBar from './components/ToolBar'

  const EditMode = {
    Create: 'create',
    Update: 'update',
    Show: 'show',
  }

  const BuiltInButtonType = {
    Show: 'show',
    Update: 'update',
    Remove: 'remove',
  }

  export default {
    name: 'ProTable',

    components: {
      NewBsBox,
      BeerForm,
      BeerTable,
      BeerButton,
      Search,
      Pagination,
      BeerModal,
      BeerIcon,
      BeerTag,
      ToolBar,
    },

    props: {
      /**
       * 列配置
       */
      columns: {
        type: Array,
        required: true,
      },

      /**
       * 工具栏标题
       */
      title: String,

      /**
       * 工具栏按钮
       */
      toolBarButtons: Array,

      /**
       * 开启分页
       */
      autoPagination: {
        type: Boolean,
        default: true,
      },

      /**
       * 自定义的筛选 form 配置，会和 columns 里的 filterable 列合并
       */
      filtersConfig: Object,

      /**
       * 查询 service，返回 promise
       */
      get: {
        type: Function,
        required: true,
      },

      /**
       * 新建 service，返回 promise
       */
      create: Function,

      /**
       * 更新 service，返回 promise
       */
      update: Function,

      /**
       * 删除 service，返回 promise
       */
      remove: Function,

      /**
       * 删除时的 confirm message
       */
      removeConfirmMessage: {
        type: String,
        default: '确定删除吗？',
      },
    },

    data() {
      return {
        tableData: [],

        filters: {},
        keywords: '',
        sorts: {},
        pagination: {
          total: 0,
          pageNo: 1,
          pageSize: 10,
        },

        editMode: undefined,
        editFormValue: {},
      }
    },

    computed: {
      buttonColumn() {
        return this.columns
          .find(column => column.td && column.td.is === 'buttons')
      },

      buttons() {
        return this.buttonColumn?.td?.buttons ?? []
      },

      tableColumns() {
        return this.columns
          .filter(column => !(column.td && column.td.is === 'buttons'))
      },

      mergedFiltersConfig() {
        const filterColumns = this.tableColumns.filter(column => column.filterable === true)
        const children = filterColumns.map(column => {
          const component = this.getComponent(column)

          if (!component.props) {
            component.props = {}
          }

          component.props.placeholder = `筛选${column.title}`

          return {
            name: column.key,
            component,
          }
        })

        if (this.filtersConfig) {
          return {
            ...this.filtersConfig,
            children: [
              ...this.filtersConfig.children,
              ...children,
            ],
          }
        }

        return {
          layout: 'Inline',
          children,
        }
      },

      newToolBarButtons() {
        if (this.create) {
          const createBtn = {
            text: '新建',
            onClick: this.handleCreateBtnClick,
          }

          if (!this.toolBarButtons) {
            return [createBtn]
          }

          return [
            ...this.toolBarButtons,
            createBtn,
          ]
        }

        return this.toolBarButtons
      },

      showEditModal() {
        return this.editMode !== undefined
      },

      editFormReadonly() {
        return this.editMode === EditMode.Show
      },

      editFormConfig() {
        return {
          children: this.tableColumns.map(column => {
            const component = this.getComponent(column)

            return {
              name: column.key,
              formItem: {
                label: column.title,
                isHorizontal: true,
              },
              component,
            }
          }),
        }
      },

      editModalTitle() {
        switch (this.editMode) {
          case 'create':
            return '新建'
          case 'update':
            return '更新'
          case 'show':
            return '查看'
        }
      },

      editModalConfirmText() {
        if (this.editMode === EditMode.Show) {
          return '编辑'
        }

        return '提交'
      },

      editModalCancelText() {
        if (this.editMode === EditMode.Show) {
          return '关闭'
        }
      },
    },

    watch: {
      pagination() {
        this.setTable()
      },
    },

    async created() {
      // 等 filters 默认值设置成功
      await this.$nextTick()
      this.setTable()
    },

    methods: {
      handleFilter() {
        this.setTable()
      },

      handleFiltersReset() {
        this.filters = {}
        this.keywords = ''
        this.setTable()
      },

      handleEditModalClose(flag) {
        if (!flag) {
          this.editMode = undefined
          this.editFormValue = {}
        }
      },

      async handleEditModalConfirm() {
        if (this.editMode === EditMode.Show) {
          this.editMode = EditMode.Update
          return
        }

        try {
          await this[this.editMode](this.editFormValue)
          this.handleEditModalClose()
          this.setTable()
        } catch (error) {
          toaster.danger(error.message)
        }
      },

      handleCreateBtnClick() {
        this.editMode = EditMode.Create
      },

      handleButtonClick(i, { rowData }) {
        const button = this.buttons[i]

        if (button.onClick === BuiltInButtonType.Show) {
          this.editMode = EditMode.Show
          this.editFormValue = rowData
        } else if (button.onClick === BuiltInButtonType.Update) {
          this.editMode = EditMode.Update
          this.editFormValue = rowData
        } else if (button.onClick === BuiltInButtonType.Remove) {
          const openDialog = withDialog({
            title: this.removeConfirmMessage,
          })

          openDialog(() => {
            this.remove(rowData)
            this.setTable()
          })
        } else {
          button.onClick(payload)
        }
      },

      async setTable() {
        try {
          const payload = {
            pagination: this.pagination,
            filters: this.filters,
            sorts: this.sorts,
            keywords: this.keywords,
          }
          const { pageNo, pageSize } = this.pagination
          const params = {
            pageNo,
            pageSize,
            ...this.filters,
          }

          if (this.keywords) {
            params.keywords = this.keywords
          }

          const sortKeys = Object.keys(this.sorts)

          if (sortKeys.length > 0) {
            params.sortBy = sortKeys.map(key => `${key} ${this.sorts[key]}`).join()
          }

          const result = await this.get(params, payload)

          this.tableData = result.list
          console.log(this.tableData)
          this.pagination.total = result.total
        } catch (error) {
          toaster.danger(error.message)
        }
      },

      getComponent(column) {
        const component = {
          props: {},
        }

        return column.inputComponent ?? component
      },
    },
  }
</script>

<template>
  <new-bs-box>
    <header>
      <beer-form
        v-if="mergedFiltersConfig.children.length > 0"
        v-model="filters"
        :config="mergedFiltersConfig"
        :submit="handleFilter"
        submit-text="查询"
      />

      <tool-bar
        :title="title"
        :buttons="newToolBarButtons"
      />
    </header>

    <main>
      <beer-table
        :columns="columns"
        :data="tableData"
      >
        <template #buttons="slotProps">
          <beer-button
            v-for="({ onClick, variant, size, ...button }, i) in buttons"
            :key="i"
            v-bind="button"
            :bs="{ p: 0, mr: 5 }"
            :variant="variant ? variant : 'text'"
            :size="size ? size : 's'"
            @click="handleButtonClick(i, slotProps)"
          >{{ button.text }}</beer-button>
        </template>
      </beer-table>

      <pagination
        v-if="autoPagination"
        v-model="pagination"
        :bs="{ mt: 15 }"
      />
    </main>

    <beer-modal
      :show="showEditModal"
      :confirm-text="editModalConfirmText"
      :cancel-text="editModalCancelText"
      size="m"
      @update:show="handleEditModalClose"
      @confirm="handleEditModalConfirm"
    >
      <template #title>
        {{ editModalTitle }}
      </template>

      <beer-form
        v-model="editFormValue"
        :config="editFormConfig"
        :readonly="editFormReadonly"
      />
    </beer-modal>
  </new-bs-box>
</template>

<style lang="stylus" scoped>
  .header,
  .filters-container
    display flex
    justify-content space-between

  .search
    margin-left 15px
</style>
